/**
 * Team driving and change-request outcome helpers. The fields are optional in pending.ts (EldEventExtras) so the
 * portal works against an API that has not published them yet.
 */
import type { DriverVehicleSession, EldEventExtras } from '@/api/pending';
import type { EldEvent } from './eld';

type WithExtras = EldEvent & EldEventExtras;

export interface CoDriverRef {
  id: string | null;
  username: string;
}

/** The record's co-driver (team driving), or null for a solo record. */
export function coDriverOf(e: EldEvent): CoDriverRef | null {
  if (!e.coDriverId && !e.coDriverUsername) return null;
  return { id: e.coDriverId ?? null, username: e.coDriverUsername ?? '' };
}

/** Distinct co-drivers over a set of records (by id, then username). */
export function coDriversOf(events: EldEvent[]): CoDriverRef[] {
  const seen = new Map<string, CoDriverRef>();
  for (const e of events) {
    const c = coDriverOf(e);
    if (c) seen.set(c.id ?? `u:${c.username}`, c);
  }
  return [...seen.values()];
}

export type RequestOutcome = 'rejected_by_driver' | 'cancelled_by_carrier';

/**
 * How a status-4 carrier change request / assignment ended. FMCSA status 4 ("change rejected") covers both a driver
 * rejection and a carrier withdrawal; the backend tells them apart on EditRequest.outcome (and changeRequestOutcome
 * for devices). `cancelledIds` = proposed-record ids from GET /v1/carrier/edit-requests?state=cancelled; while it is
 * unknown (null) this returns null rather than guessing.
 */
export function requestOutcomeOf(e: EldEvent, cancelledIds: ReadonlySet<string> | null): RequestOutcome | null {
  const marker = (e as WithExtras).changeRequestOutcome;
  if (marker) return marker;
  if (e.recordStatus !== 4 || !e.supersedesEventId || (e.recordOrigin !== 3 && e.recordOrigin !== 4)) return null;
  if (!cancelledIds) return null;
  return cancelledIds.has(e.id) ? 'cancelled_by_carrier' : 'rejected_by_driver';
}

export interface TeamPair {
  vehicleId: string;
  unitNumber: string;
  members: DriverVehicleSession[];
}

/** Vehicles with two or more drivers signed in (or a session naming a co-driver), from open sessions. */
export function teamPairs(sessions: DriverVehicleSession[]): TeamPair[] {
  const byVehicle = new Map<string, TeamPair>();
  for (const s of sessions) {
    if (s.endedAt) continue;
    const p = byVehicle.get(s.vehicleId) ?? { vehicleId: s.vehicleId, unitNumber: s.unitNumber, members: [] };
    p.members.push(s);
    byVehicle.set(s.vehicleId, p);
  }
  const roleOrder = (s: DriverVehicleSession) => (s.role === 'driver' ? 0 : 1);
  return [...byVehicle.values()]
    .filter((p) => p.members.length > 1 || p.members.some((m) => m.coDriverId))
    .map((p) => ({ ...p, members: [...p.members].sort((a, b) => roleOrder(a) - roleOrder(b)) }))
    .sort((a, b) => a.unitNumber.localeCompare(b.unitNumber, 'en', { numeric: true }));
}

/** The other open session on the same vehicle as `driverId` (their team mate), or null. */
export function teamMateOf(driverId: string, sessions: DriverVehicleSession[] | undefined): DriverVehicleSession | null {
  const mine = sessions?.find((s) => s.driverId === driverId && !s.endedAt);
  if (!mine) return null;
  return sessions?.find((s) => s.vehicleId === mine.vehicleId && s.driverId !== driverId && !s.endedAt) ?? null;
}
